import { useEffect, useRef } from "react";

/**
 * Signature cursor follower:
 * - A tiny Klein-blue dot pinned exactly to the pointer
 * - A larger ink-outlined ring that lags behind with a smooth spring
 * - Ring grows + fills Klein blue when hovering interactive elements
 * - Disabled on touch / coarse-pointer devices (native cursor stays)
 */
export default function CustomCursor() {
  const dotRef = useRef(null);
  const ringRef = useRef(null);

  useEffect(() => {
    if (window.matchMedia("(pointer: coarse)").matches) return;

    const html = document.documentElement;
    html.classList.add("has-custom-cursor");

    const pos = { x: window.innerWidth / 2, y: window.innerHeight / 2 };
    const ring = { x: pos.x, y: pos.y };
    let hovering = false;
    let raf;

    const interactiveSelector =
      'a, button, [role="button"], [data-testid^="hero-sticker-"], label, select, summary, ' +
      'input[type="submit"], input[type="button"], input[type="checkbox"], input[type="radio"], input[type="file"]';

    const onMove = (e) => {
      pos.x = e.clientX;
      pos.y = e.clientY;
      if (dotRef.current) {
        dotRef.current.style.transform = `translate3d(${pos.x}px, ${pos.y}px, 0) translate(-50%, -50%)`;
      }
    };

    const onOver = (e) => {
      const el = e.target;
      if (el && el.closest && el.closest(interactiveSelector)) {
        hovering = true;
        ringRef.current?.classList.add("ec-hover");
        dotRef.current?.classList.add("ec-hover");
      }
    };

    const onOut = (e) => {
      const el = e.target;
      if (el && el.closest && el.closest(interactiveSelector)) {
        hovering = false;
        ringRef.current?.classList.remove("ec-hover");
        dotRef.current?.classList.remove("ec-hover");
      }
    };

    const loop = () => {
      ring.x += (pos.x - ring.x) * 0.18;
      ring.y += (pos.y - ring.y) * 0.18;
      if (ringRef.current) {
        const scale = hovering ? 1.6 : 1;
        ringRef.current.style.transform = `translate3d(${ring.x}px, ${ring.y}px, 0) translate(-50%, -50%) scale(${scale})`;
      }
      raf = requestAnimationFrame(loop);
    };

    const onLeave = () => {
      if (dotRef.current) dotRef.current.style.opacity = "0";
      if (ringRef.current) ringRef.current.style.opacity = "0";
    };
    const onEnter = () => {
      if (dotRef.current) dotRef.current.style.opacity = "1";
      if (ringRef.current) ringRef.current.style.opacity = "1";
    };

    document.addEventListener("mousemove", onMove, { passive: true });
    document.addEventListener("mouseover", onOver, { passive: true });
    document.addEventListener("mouseout", onOut, { passive: true });
    document.addEventListener("mouseleave", onLeave);
    document.addEventListener("mouseenter", onEnter);
    raf = requestAnimationFrame(loop);

    return () => {
      html.classList.remove("has-custom-cursor");
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseover", onOver);
      document.removeEventListener("mouseout", onOut);
      document.removeEventListener("mouseleave", onLeave);
      document.removeEventListener("mouseenter", onEnter);
      cancelAnimationFrame(raf);
    };
  }, []);

  return (
    <>
      <div ref={ringRef} className="ec-ring" aria-hidden="true" />
      <div ref={dotRef} className="ec-dot" aria-hidden="true" />
    </>
  );
}
